import {useCallback} from 'react';
import assert from 'assert-ts';
import {useNavigate} from 'react-router';
import type {BulkUpdateEntityType, MetadataId} from 'types';
import {Concept} from 'types';
import {useLocalization} from 'localization';
import {getResourcesBatched} from 'api/functions';
import {useAppDispatch} from 'store/hooks';
import {useSnacks} from 'components';
import {MASSUPDATE_BATCH_SIZE} from 'scenes/advancedSearch/consts';
import {mapMetadataIdByType} from '../mapper';
import {bulkUpdateItemsUpdated} from '../reducer';

export const useCreateBulkUpdate = (
  type: BulkUpdateEntityType,
  total: number,
  searcher: (page: number, size: number) => Promise<MetadataId[]>,
  reportProgress: (total: number, batchSize: number, progress: number) => void,
) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {errorSnack} = useSnacks();
  const {t} = useLocalization();

  return useCallback((): Promise<void> => {
    if (type !== Concept.work) {
      assert(false, 'Not implemented, only work');
    }

    return getResourcesBatched(
      total,
      MASSUPDATE_BATCH_SIZE,
      searcher,
      reportProgress,
    )
      .then(ids => {
        if (ids.length !== total) {
          errorSnack(
            t('page.bulkUpdate.error.count_mismatch', {
              loadCount: ids.length,
              searchCount: total,
            }),
          );

          return;
        }

        dispatch(
          bulkUpdateItemsUpdated({
            type: type,
            itemIds: mapMetadataIdByType(type, ids),
          }),
        );

        navigate(`/bulkUpdate/new`);
      })
      .catch(error => {
        errorSnack(t('page.bulkUpdate.error.load_failed'), error);
      });
  }, [
    dispatch,
    errorSnack,
    navigate,
    reportProgress,
    searcher,
    t,
    total,
    type,
  ]);
};
